import React, { useEffect, useRef, useState, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Api from '../../../Api';
import * as Sentry from '@sentry/browser';
import { InputHelper } from '../../Utils/FieldsHelper';
import classes from './styles.module.css';
import MessageContext from '../../../MessageContext';
import { getAuthSchema } from './authSchemas';
import { shouldShowMethodsBlock, capabilityAllows } from './capabilities';

/**
 * Рендерит секцию "Ключи интеграции" для терминала.
 *
 * Если для протокола есть схема в authSchemas — рендерим поля с русскими лейблами,
 * textarea для PEM-ключей, поддержка двух источников (processingAuth vs nodaBank).
 *
 * Если протокол неизвестен — fallback на старый flat-вью processingAuth
 * (все ключи как plain input). Это даёт обратную совместимость для старых
 * протоколов которые не добавлены в схему.
 */
function renderAuthFields(terminal, form, onAuthFieldChange, isSaving) {
    const protocol = terminal && terminal.processingProtocol;
    const schema = getAuthSchema(protocol);

    if (schema) {
        return renderSchemaAuth(schema, form, onAuthFieldChange, isSaving);
    }
    return renderLegacyAuth(form, onAuthFieldChange, isSaving);
}

function renderSchemaAuth(schema, form, onAuthFieldChange, isSaving) {
    // Делим схему на основную часть и "advanced" (свёрнутая секция).
    // Advanced включает тестовые fallback-значения (ФИО получателя default,
    // счёт отправителя default), которые мешают в основном UI.
    const main = [];
    const advanced = [];
    let advancedSection = null;
    schema.forEach(function (item) {
        if (item.advanced) {
            if (item.section) {
                advancedSection = item;
            } else {
                advanced.push(item);
            }
        } else {
            main.push(item);
        }
    });
    return (
        <div>
            {renderSchemaGrid(main, form, onAuthFieldChange, isSaving, 'main')}
            {advanced.length > 0 && (
                <details style={{ marginTop: 12 }}>
                    <summary
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#2479E0',
                            cursor: 'pointer',
                            padding: '6px 0',
                        }}
                    >
                        {(advancedSection && advancedSection.section) || 'Дополнительные настройки'}
                    </summary>
                    {advancedSection && advancedSection.advancedHint && (
                        <div
                            style={{
                                fontSize: 12,
                                color: '#666',
                                margin: '4px 0 10px',
                                lineHeight: 1.5,
                            }}
                        >
                            {advancedSection.advancedHint}
                        </div>
                    )}
                    {renderSchemaGrid(advanced, form, onAuthFieldChange, isSaving, 'adv')}
                </details>
            )}
        </div>
    );
}

function renderSchemaGrid(schemaItems, form, onAuthFieldChange, isSaving, keyPrefix) {
    const items = [];
    let idx = 0;
    schemaItems.forEach(function (item) {
        if (item.section) {
            items.push(
                <div
                    key={keyPrefix + '-section-' + idx++}
                    style={{
                        gridColumn: '1 / -1',
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#2479E0',
                        marginTop: 8,
                        marginBottom: 4,
                    }}
                >
                    {item.section}
                </div>
            );
            return;
        }
        const source = item.source || 'processingAuth';
        const bucket = (form && form[source]) || {};
        const value = bucket[item.key] != null ? bucket[item.key] : '';
        const inputProps = {
            value: value,
            onChange: function (e) {
                onAuthFieldChange(source, item.key, e.target.value);
            },
            placeholder: item.placeholder || '',
            disabled: isSaving,
            style: { width: '100%' },
        };
        items.push(
            <label
                key={keyPrefix + '-' + source + '.' + item.key}
                style={item.multiline ? { gridColumn: '1 / -1' } : {}}
            >
                <span style={{ display: 'block', fontSize: 12, color: '#444', marginBottom: 2 }}>{item.label}</span>
                {item.multiline ? (
                    <textarea
                        {...inputProps}
                        rows={6}
                        style={{
                            width: '100%',
                            fontFamily: 'monospace',
                            fontSize: 11,
                            resize: 'vertical',
                        }}
                    />
                ) : (
                    <input type="text" {...inputProps} />
                )}
                {item.hint && (
                    <span style={{ display: 'block', fontSize: 11, color: '#888', marginTop: 2 }}>{item.hint}</span>
                )}
            </label>
        );
    });
    return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{items}</div>;
}

function renderLegacyAuth(form, onAuthFieldChange, isSaving) {
    const keys = Object.keys((form && form.processingAuth) || {});
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {keys.length === 0 && <div style={{ gridColumn: '1 / -1', opacity: 0.7 }}>Нет ключей авторизации</div>}
            {keys.map(function (k) {
                return (
                    <label key={k}>
                        {k}
                        <input
                            type="text"
                            value={(form.processingAuth && form.processingAuth[k]) || ''}
                            onChange={function (e) {
                                onAuthFieldChange('processingAuth', k, e.target.value);
                            }}
                            style={{ width: '100%' }}
                            disabled={isSaving}
                        />
                    </label>
                );
            })}
        </div>
    );
}

function TerminalEditing(props) {
    const intl = useIntl();
    const terminal = props.terminal || null;
    const token = props.token;

    const [isSaving, setIsSaving] = useState(false);
    const [isLimitsSaving, setIsLimitsSaving] = useState(false);
    const [isLimitsLoading, setIsLimitsLoading] = useState(false);
    const [form, setForm] = useState({});
    const [limits, setLimits] = useState({
        payment: { min: null, max: null, daily: null, monthly: null },
        payout: { min: null, max: null, daily: null, monthly: null },
        velocity: { perMinute: null, perHour: null, perDay: null },
    });
    const [payoutLimit, setPayoutLimit] = useState(null);
    const mounted = useRef(true);
    const { onMessage } = useContext(MessageContext);
    const [providers, setProviders] = useState([]);

    useEffect(() => {
        mounted.current = true;

        if (terminal && terminal.id) {
            const raw = terminal.rawData || terminal.data || {};
            setForm({
                id: terminal.id,
                projectUuid: terminal.merchantUuid || terminal.projectUuid || '',
                name: terminal.name || '',
                status: typeof terminal.status === 'number' ? terminal.status : 1,
                processingAuth: raw.processingAuth || terminal.processingAuth || {},
                nodaBank: raw.nodaBank || {},
                bankAccount: raw.bankAccount || terminal.bankAccount || '',
                paymentMethod: raw.paymentMethod || terminal.paymentMethod || '',
                returnUrl: raw.returnUrl || terminal.returnUrl || '',
                webhookUrl: raw.webhookUrl || terminal.webhookUrl || '',
                exchangeRate:
                    raw.exchangeRate !== undefined
                        ? raw.exchangeRate
                        : typeof terminal.exchangeRate === 'number' || terminal.exchangeRate === null
                          ? terminal.exchangeRate
                          : null,
                isSBP: !!(raw.isSBP !== undefined ? raw.isSBP : terminal.isSBP),
                preAuth: !!(raw.preAuth !== undefined ? raw.preAuth : terminal.preAuth),
                recurring: !!(raw.recurring !== undefined ? raw.recurring : terminal.recurring),
                partialRefund: !!(raw.partialRefund !== undefined ? raw.partialRefund : terminal.partialRefund),
                partialReversal: !!(raw.partialReversal !== undefined ? raw.partialReversal : terminal.partialReversal),
                h2h: !!(raw.h2h !== undefined ? raw.h2h : terminal.h2h),
                crmPayoutEnabled: !!raw.crmPayoutEnabled,
                crmSbpPayoutEnabled: !!raw.crmSbpPayoutEnabled,
                providerUuid: terminal.providerUuid || '',
            });

            setIsLimitsLoading(true);
            new Api({ token })
                .getTerminalLimits(terminal.merchantUuid || terminal.projectUuid, terminal.id)
                .then((r) => {
                    if (mounted.current && r && r.data && r.data.success) {
                        const defaultLimits = {
                            payment: { min: null, max: null, daily: null, monthly: null },
                            payout: { min: null, max: null, daily: null, monthly: null },
                            velocity: { perMinute: null, perHour: null, perDay: null },
                        };

                        const loaded = r.data.limits || {};
                        const merged = {
                            payment: { ...defaultLimits.payment, ...(loaded.payment || {}) },
                            payout: { ...defaultLimits.payout, ...(loaded.payout || {}) },
                            velocity: { ...defaultLimits.velocity, ...(loaded.velocity || {}) },
                        };

                        setLimits(merged);
                        setPayoutLimit(r.data.payoutLimit != null ? r.data.payoutLimit : null);
                    }
                })
                .catch((err) => {
                    Sentry.captureException(err);
                    onMessage({
                        type: 'error',
                        message: intl.formatMessage({ id: 'TerminalEditing.error.loadLimits' }),
                    });
                })
                .finally(() => {
                    if (mounted.current) {
                        setIsLimitsLoading(false);
                    }
                });
        } else {
            setForm({});
            setLimits({
                payment: { min: null, max: null, daily: null, monthly: null },
                payout: { min: null, max: null, daily: null, monthly: null },
                velocity: { perMinute: null, perHour: null, perDay: null },
            });
            setPayoutLimit(null);
            setIsLimitsLoading(false);
        }

        new Api({ token })
            .getAvailableProviders()
            .then((r) => {
                const items = r && r.data ? r.data : [];
                setProviders(items);
            })
            .catch((err) => {
                Sentry.captureException(err);
                setProviders([]);
            });

        return () => {
            mounted.current = false;
        };
    }, [terminal, token]);

    function onChange(name, value) {
        setForm((prev) => ({ ...prev, [name]: value }));
    }

    /**
     * Обновление поля auth по source ('processingAuth' или 'nodaBank').
     * Используется секцией "Ключи интеграции" с per-protocol схемой.
     */
    function onAuthFieldChange(source, key, value) {
        setForm((prev) => ({
            ...prev,
            [source]: { ...(prev[source] || {}), [key]: value },
        }));
    }

    function onLimitChange(section, field, value) {
        const num = value === '' ? null : Number(value);
        setLimits((prev) => ({
            ...prev,
            [section]: { ...prev[section], [field]: num },
        }));
    }

    async function save() {
        if (!form.id || !form.projectUuid) return;
        setIsSaving(true);
        const api = new Api({ token });

        try {
            const payload = {
                name: form.name,
                status: parseInt(form.status, 10),
                processingAuth: form.processingAuth || {},
                data: {
                    isSBP: !!form.isSBP,
                    preAuth: !!form.preAuth,
                    recurring: !!form.recurring,
                    partialRefund: !!form.partialRefund,
                    partialReversal: !!form.partialReversal,
                    paymentMethod: form.paymentMethod || null,
                    h2h: !!form.h2h,
                    crmPayoutEnabled: !!form.crmPayoutEnabled,
                    crmSbpPayoutEnabled: !!form.crmSbpPayoutEnabled,
                    returnUrl: form.returnUrl || null,
                    webhookUrl: form.webhookUrl || null,
                    exchangeRate: form.exchangeRate !== '' ? form.exchangeRate : null,
                    bankAccount: form.bankAccount || null,
                    nodaBank: form.nodaBank && Object.keys(form.nodaBank).length > 0 ? form.nodaBank : undefined,
                },
            };

            await api.updateTerminal(form.projectUuid, form.id, payload);

            if (form.providerUuid && terminal && terminal.providerUuid !== form.providerUuid) {
                await api.updateServiceProvider(form.projectUuid, form.id, form.providerUuid);
            }

            onMessage({
                type: 'success',
                message: intl.formatMessage({ id: 'TerminalEditing.success.saved' }),
            });
            if (typeof props.onSaved === 'function') props.onSaved();
        } catch (e) {
            Sentry.captureException(e);
            onMessage({
                type: 'error',
                message: intl.formatMessage({ id: 'TerminalEditing.error.save' }),
            });
        } finally {
            setIsSaving(false);
        }
    }

    async function saveLimits() {
        if (!form.id || !form.projectUuid) return;
        setIsLimitsSaving(true);
        const api = new Api({ token });

        const payload = {
            limits: {
                payment: Object.fromEntries(
                    Object.entries(limits.payment).map(([k, v]) => [k, v === null ? null : Number(v)])
                ),
                payout: Object.fromEntries(
                    Object.entries(limits.payout).map(([k, v]) => [k, v === null ? null : Number(v)])
                ),
                velocity: Object.fromEntries(
                    Object.entries(limits.velocity).map(([k, v]) => [k, v === null ? null : Number(v)])
                ),
            },
            payoutLimit: payoutLimit === null || payoutLimit === '' ? null : Number(payoutLimit),
        };

        try {
            await api.updateTerminalLimits(form.projectUuid, form.id, payload);
            onMessage({
                type: 'success',
                message: intl.formatMessage({ id: 'TerminalEditing.success.limitsSaved' }),
            });
        } catch (e) {
            Sentry.captureException(e);
            onMessage({
                type: 'error',
                message: intl.formatMessage({ id: 'TerminalEditing.error.saveLimits' }),
            });
        } finally {
            setIsLimitsSaving(false);
        }
    }

    const isSelected = !!(terminal && terminal.id);

    const renderLimitInput = (section, field, labelId) => {
        const sectionData = limits[section] || {};
        const value = sectionData[field];
        const displayValue = value == null || value === 0 ? '' : value;

        return (
            <label>
                <FormattedMessage id={labelId} />
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={displayValue}
                    onChange={(e) => onLimitChange(section, field, e.target.value)}
                    placeholder={intl.formatMessage({ id: 'TerminalEditing.placeholder.notSet' })}
                    style={{ width: '100%' }}
                    disabled={isLimitsSaving || isLimitsLoading}
                />
            </label>
        );
    };

    return (
        <div className={classes.Card}>
            {/* Оверлей загрузки данных */}
            {isLimitsLoading && isSelected && (
                <div className={classes.SavingOverlay}>
                    <div className={classes.SavingSpinner}></div>
                    <span>Загрузка...</span>
                </div>
            )}
            {/* Оверлей блокировки при сохранении */}
            {isSaving && (
                <div className={classes.SavingOverlay}>
                    <div className={classes.SavingSpinner}></div>
                    <span>Сохранение...</span>
                </div>
            )}

            {isSelected ? (
                <React.Fragment>
                    <div className={classes.Content}>
                        <div className={classes.TerminalHeader}>
                            <div className={classes.TerminalHeaderLeft}>
                                <h3 className={classes.TerminalHeaderTitle}>
                                    {(terminal && (terminal.name || form.name)) || 'Терминал'}
                                </h3>
                                <div className={classes.TerminalHeaderMeta}>
                                    {terminal && terminal.processingProtocol && (
                                        <span className={classes.ProtocolBadge}>
                                            <span className={classes.ProtocolBadgeDot} />
                                            {terminal.processingName || terminal.processingProtocol}
                                        </span>
                                    )}
                                    {terminal && terminal.id && (
                                        <span className={classes.TerminalIdLabel}>ID: {terminal.id}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className={classes.ProjectFields}>
                            <InputHelper
                                onChange={onChange}
                                value={form.name || ''}
                                fieldName="name"
                                label="TerminalEditing.field.name"
                                isDisabled={isSaving}
                            />

                            <label>
                                <FormattedMessage id="TerminalEditing.field.status" defaultMessage="Статус" />
                                <select
                                    value={typeof form.status === 'number' ? form.status : 1}
                                    onChange={(e) => onChange('status', parseInt(e.target.value, 10))}
                                    style={{ width: '100%' }}
                                    disabled={isSaving}
                                >
                                    <option value={1}>Активен</option>
                                    <option value={0}>Неактивен</option>
                                </select>
                            </label>

                            {shouldShowMethodsBlock(terminal) && (
                                <React.Fragment>
                                    <div className={classes.Divider} />

                                    <h5 className={classes.BlockTitle} style={{ marginTop: 0 }}>
                                        <FormattedMessage
                                            id="TerminalEditing.section.methods"
                                            defaultMessage="Методы"
                                        />
                                    </h5>
                                    <div className={classes.MethodsGrid}>
                                        {/*
                                          СБП/Рекуррент/H2H — настройки мерчанта поверх протокола,
                                          оставляем для всех card-протоколов.
                                          preAuth/partialRefund/partialReversal — capabilities самого
                                          протокола, показываем только если он реально умеет.
                                        */}
                                        <label className={classes.ToggleLabel}>
                                            <input
                                                type="checkbox"
                                                checked={!!form.isSBP}
                                                onChange={(e) => onChange('isSBP', e.target.checked)}
                                            />
                                            <span className={classes.ToggleSwitch}></span>
                                            <span className={classes.ToggleText}>СБП</span>
                                        </label>
                                        <label className={classes.ToggleLabel}>
                                            <input
                                                type="checkbox"
                                                checked={!!form.crmPayoutEnabled}
                                                onChange={(e) => onChange('crmPayoutEnabled', e.target.checked)}
                                            />
                                            <span className={classes.ToggleSwitch}></span>
                                            <span className={classes.ToggleText}>CRM выплаты</span>
                                        </label>
                                        <label className={classes.ToggleLabel}>
                                            <input
                                                type="checkbox"
                                                checked={!!form.crmSbpPayoutEnabled}
                                                disabled={!form.crmPayoutEnabled}
                                                onChange={(e) => onChange('crmSbpPayoutEnabled', e.target.checked)}
                                            />
                                            <span className={classes.ToggleSwitch}></span>
                                            <span className={classes.ToggleText}>CRM выплаты СБП</span>
                                        </label>
                                        {capabilityAllows(terminal, 'preAuth') && (
                                            <label className={classes.ToggleLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!form.preAuth}
                                                    onChange={(e) => onChange('preAuth', e.target.checked)}
                                                />
                                                <span className={classes.ToggleSwitch}></span>
                                                <span className={classes.ToggleText}>Предавторизация</span>
                                            </label>
                                        )}
                                        {capabilityAllows(terminal, 'partialRefund') && (
                                            <label className={classes.ToggleLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!form.partialRefund}
                                                    onChange={(e) => onChange('partialRefund', e.target.checked)}
                                                />
                                                <span className={classes.ToggleSwitch}></span>
                                                <span className={classes.ToggleText}>Частичный возврат</span>
                                            </label>
                                        )}
                                        {capabilityAllows(terminal, 'partialReversal') && (
                                            <label className={classes.ToggleLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!form.partialReversal}
                                                    onChange={(e) => onChange('partialReversal', e.target.checked)}
                                                />
                                                <span className={classes.ToggleSwitch}></span>
                                                <span className={classes.ToggleText}>Частичная отмена</span>
                                            </label>
                                        )}
                                        <label className={classes.ToggleLabel}>
                                            <input
                                                type="checkbox"
                                                checked={!!form.recurring}
                                                onChange={(e) => onChange('recurring', e.target.checked)}
                                            />
                                            <span className={classes.ToggleSwitch}></span>
                                            <span className={classes.ToggleText}>Рекуррент</span>
                                        </label>
                                        <label className={classes.ToggleLabel}>
                                            <input
                                                type="checkbox"
                                                checked={!!form.h2h}
                                                onChange={(e) => onChange('h2h', e.target.checked)}
                                            />
                                            <span className={classes.ToggleSwitch}></span>
                                            <span className={classes.ToggleText}>
                                                <FormattedMessage
                                                    id="TerminalEditing.method.h2h"
                                                    defaultMessage="Человек к человеку"
                                                />
                                            </span>
                                        </label>
                                    </div>
                                </React.Fragment>
                            )}

                            <div className={classes.Divider} />

                            <h5 className={classes.BlockTitle} style={{ marginTop: 0 }}>
                                <FormattedMessage
                                    id="TerminalEditing.section.provider"
                                    defaultMessage="Привязка провайдера"
                                />
                            </h5>
                            <div>
                                <select
                                    value={form.providerUuid || ''}
                                    onChange={(e) => onChange('providerUuid', e.target.value)}
                                    disabled={isSaving}
                                >
                                    <option value="">
                                        {intl.formatMessage({ id: 'TerminalEditing.provider.noChange' })}
                                    </option>
                                    {providers.map((p) => (
                                        <option key={p && p.uuid} value={p && p.uuid}>
                                            {(p && p.name) || ''}
                                        </option>
                                    ))}
                                </select>
                                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
                                    <FormattedMessage
                                        id="TerminalEditing.provider.current"
                                        defaultMessage="Текущий: {name}"
                                        values={{ name: (terminal && terminal.providerName) || '—' }}
                                    />
                                </div>
                            </div>

                            <div className={classes.Divider} />

                            <h5 className={classes.BlockTitle} style={{ marginTop: 0 }}>
                                Ключи интеграции
                            </h5>
                            {renderAuthFields(terminal, form, onAuthFieldChange, isSaving)}

                            {shouldShowMethodsBlock(terminal) && (
                                <React.Fragment>
                                    <div className={classes.Divider} />
                                    <h5 className={classes.BlockTitle} style={{ marginTop: 0 }}>
                                        <FormattedMessage id="TerminalEditing.section.other" defaultMessage="Прочее" />
                                    </h5>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                        <label>
                                            Банковский счёт
                                            <input
                                                type="text"
                                                value={form.bankAccount || ''}
                                                onChange={(e) => onChange('bankAccount', e.target.value)}
                                            />
                                        </label>
                                        <label>
                                            Способ оплаты
                                            <input
                                                type="text"
                                                value={form.paymentMethod || ''}
                                                onChange={(e) => onChange('paymentMethod', e.target.value)}
                                            />
                                        </label>
                                        <label>
                                            Адрес возврата
                                            <input
                                                type="text"
                                                value={form.returnUrl || ''}
                                                onChange={(e) => onChange('returnUrl', e.target.value)}
                                            />
                                        </label>
                                        <label>
                                            Адрес колбека
                                            <input
                                                type="text"
                                                value={form.webhookUrl || ''}
                                                onChange={(e) => onChange('webhookUrl', e.target.value)}
                                            />
                                        </label>
                                        <label>
                                            Курс обмена
                                            <input
                                                type="number"
                                                step="0.0001"
                                                value={form.exchangeRate === null ? '' : form.exchangeRate}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    onChange('exchangeRate', val === '' ? null : parseFloat(val));
                                                }}
                                            />
                                        </label>
                                    </div>
                                </React.Fragment>
                            )}

                            {terminal && terminal.processingProtocol === 'Psb' && (
                                <React.Fragment>
                                    <div className={classes.Divider} />
                                    <h5 className={classes.BlockTitle} style={{ marginTop: 0 }}>
                                        ПСБ интеграция
                                    </h5>
                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const r = await new Api({ token }).registerPsbMirAccept([
                                                        terminal.id,
                                                    ]);
                                                    const result = r && r.data && r.data[terminal.id];
                                                    if (result && result.ok) {
                                                        const status = result.response && result.response.status;
                                                        onMessage({
                                                            type: 'success',
                                                            message:
                                                                'MirAccept: ' +
                                                                (status === 'already exists'
                                                                    ? 'уже зарегистрирован'
                                                                    : 'отправлено в очередь регистрации (НСПК отправит в 23:00 МСК)'),
                                                        });
                                                    } else {
                                                        const err = result
                                                            ? result.err || JSON.stringify(result)
                                                            : 'unknown error';
                                                        onMessage({
                                                            type: 'error',
                                                            message: 'MirAccept ошибка: ' + err,
                                                        });
                                                    }
                                                } catch (e) {
                                                    Sentry.captureException(e);
                                                    onMessage({
                                                        type: 'error',
                                                        message: 'MirAccept: ' + (e.message || 'request failed'),
                                                    });
                                                }
                                            }}
                                            disabled={isSaving}
                                            style={{ padding: '0.5rem 1rem' }}
                                        >
                                            Зарегистрировать в MirAccept 2.0
                                        </button>
                                        {form.processingAuth && form.processingAuth.psb_miraccept_registered_at && (
                                            <span style={{ fontSize: 12, opacity: 0.7 }}>
                                                Последняя регистрация: {form.processingAuth.psb_miraccept_registered_at}
                                                {form.processingAuth.psb_miraccept_status
                                                    ? ' (' + form.processingAuth.psb_miraccept_status + ')'
                                                    : ''}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
                                        Регистрация нужна один раз. Без неё не пройдут платежи картой МИР через ПСБ.
                                        НСПК подтверждает регистрацию в течение 2 рабочих дней.
                                    </div>
                                </React.Fragment>
                            )}

                            <div className={classes.Divider} />
                            <h5 className={classes.BlockTitle} style={{ marginTop: 0 }}>
                                <FormattedMessage id="TerminalEditing.section.limits" defaultMessage="Лимиты" />
                            </h5>

                            {isLimitsLoading ? (
                                <div style={{ padding: '1.5rem', textAlign: 'center', color: '#999' }}>
                                    <div
                                        className={classes.Spinner}
                                        style={{
                                            display: 'inline-block',
                                            width: '20px',
                                            height: '20px',
                                            border: '2px solid #f3f3f3',
                                            borderTop: '2px solid #2196f3',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite',
                                            margin: '0 auto 8px',
                                        }}
                                    ></div>
                                    <div>
                                        <FormattedMessage
                                            id="TerminalEditing.loading.limits"
                                            defaultMessage="Загрузка лимитов..."
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className={classes.LimitsGrid}>
                                        <div>
                                            <strong>
                                                <FormattedMessage
                                                    id="TerminalEditing.limits.payment.title"
                                                    defaultMessage="Платежи"
                                                />
                                            </strong>
                                            <div className={classes.LimitsSubGrid2}>
                                                {renderLimitInput('payment', 'min', 'TerminalEditing.limits.min')}
                                                {renderLimitInput('payment', 'max', 'TerminalEditing.limits.max')}
                                                {renderLimitInput('payment', 'daily', 'TerminalEditing.limits.daily')}
                                                {renderLimitInput(
                                                    'payment',
                                                    'monthly',
                                                    'TerminalEditing.limits.monthly'
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <strong>
                                                <FormattedMessage
                                                    id="TerminalEditing.limits.payout.title"
                                                    defaultMessage="Выплаты"
                                                />
                                            </strong>
                                            <div className={classes.LimitsSubGrid2}>
                                                {renderLimitInput('payout', 'min', 'TerminalEditing.limits.min')}
                                                {renderLimitInput('payout', 'max', 'TerminalEditing.limits.max')}
                                                {renderLimitInput('payout', 'daily', 'TerminalEditing.limits.daily')}
                                                {renderLimitInput(
                                                    'payout',
                                                    'monthly',
                                                    'TerminalEditing.limits.monthly'
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <strong>
                                                <FormattedMessage
                                                    id="TerminalEditing.limits.velocity.title"
                                                    defaultMessage="Скорость"
                                                />
                                            </strong>
                                            <div className={classes.LimitsSubGrid3}>
                                                {renderLimitInput(
                                                    'velocity',
                                                    'perMinute',
                                                    'TerminalEditing.limits.perMinute'
                                                )}
                                                {renderLimitInput(
                                                    'velocity',
                                                    'perHour',
                                                    'TerminalEditing.limits.perHour'
                                                )}
                                                {renderLimitInput(
                                                    'velocity',
                                                    'perDay',
                                                    'TerminalEditing.limits.perDay'
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <strong>
                                                <FormattedMessage
                                                    id="TerminalEditing.limits.payoutLimit.title"
                                                    defaultMessage="Общий лимит выплат"
                                                />
                                            </strong>
                                            <div style={{ marginTop: 6 }}>
                                                <label>
                                                    <FormattedMessage
                                                        id="TerminalEditing.limits.amount"
                                                        defaultMessage="Сумма"
                                                    />
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={
                                                            payoutLimit == null || payoutLimit === 0 ? '' : payoutLimit
                                                        }
                                                        onChange={(e) =>
                                                            setPayoutLimit(
                                                                e.target.value === '' ? null : Number(e.target.value)
                                                            )
                                                        }
                                                        placeholder={intl.formatMessage({
                                                            id: 'TerminalEditing.placeholder.notSet',
                                                        })}
                                                        style={{ width: '100%' }}
                                                        disabled={isLimitsSaving}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                                        <button
                                            className={classes.BtnPrimary}
                                            onClick={saveLimits}
                                            disabled={isLimitsSaving}
                                        >
                                            {isLimitsSaving ? 'Сохранение...' : 'Сохранить лимиты'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={classes.CardFooter}>
                        <button className={classes.BtnPrimary} onClick={save} disabled={isSaving}>
                            {isSaving ? 'Сохранение...' : 'Сохранить'}
                        </button>
                    </div>
                </React.Fragment>
            ) : (
                <div className={classes.Content}>
                    <div className={classes.Empty}>
                        <h5>
                            <FormattedMessage id="TerminalEditing.empty" defaultMessage="Терминал не выбран" />
                        </h5>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TerminalEditing;



// WEBPACK FOOTER //
// ./src/Components/ComponentsOfAdministrator/TerminalEditing/index.js